import { Flex, Card, Modal, Typography } from "antd"
import { useCallback, useEffect, useMemo, useReducer } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { debounce } from "lodash"
import moment from "moment"
import PropTypes from "prop-types"

import { getSignedWaivers } from "store/clients"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import useLoaderWatchers from "modules/loader-watchers/hooks/useLoaderWatchers"
import SearchInput from "ui/SearchInput"
import WithEmptyState from "ui/WithEmptyStateWrapper"
import { Title } from "ui/Typography"
import PdfViewer from "ui/PdfViewer"

import WaiversTable from "./WaiversTable"
import { reducer, initialState, ACTIONS } from "./reducer"
import styles from "../index.module.css"

const { Text } = Typography

const WaiversTab = ({ active, id }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [state, localDispatch] = useReducer(reducer, initialState)

  const { searchBy, searchQuery, page, pageSize, sortKey, sortOrder, isAppend, isFiltered, selectedWaiver, docIndex } = state
  const pdfUrls = selectedWaiver?.signed_pdf_url ? [selectedWaiver.signed_pdf_url] : []
  const { data, metadata } = useSelector((store) => store.clients.signedWaivers)

  useLoaderWatchers({ watchers: ["clients.signedWaivers"] })

  useEffect(() => {
    if (!active || !id) return
    const params = { page, per_page: pageSize }
    if (searchQuery) params.search = searchQuery
    if (sortKey) { params.sort = sortKey; params.direction = sortOrder }
    dispatch(getSignedWaivers(id, isAppend, params))
  }, [active, id, page, pageSize, sortKey, sortOrder, searchQuery, isAppend])

  const debouncedSearch = useMemo(
    () => debounce((value) => localDispatch({ type: ACTIONS.SET_SEARCH_QUERY, payload: value }), 300),
    []
  )

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch])

  const handleSearchChange = useCallback((value) => {
    localDispatch({ type: ACTIONS.SET_SEARCH, payload: value })
    debouncedSearch(value)
  }, [debouncedSearch])

  const handleSortChange = useCallback((_pagination, _filters, sorter) =>
    localDispatch({
      type: ACTIONS.SET_SORT,
      payload: { sortKey: sorter?.field, sortOrder: sorter?.order === "ascend" ? "asc" : "desc" }
    }), [])

  const handleRowClick = useCallback((waiver) => localDispatch({ type: ACTIONS.SELECT_WAIVER, payload: waiver }), [])
  const handleCloseModal = useCallback(() => localDispatch({ type: ACTIONS.CLOSE_MODAL }), [])

  return (
    <>
      <FirstLoading name="clients.signedWaivers">
        <Card>
          <Flex vertical gap={16}>
            <Title level={5}>{t("client.signed_waivers")}</Title>
            <SearchInput
              className={styles.clientSearchInput}
              searchBy={searchBy}
              setSearchBy={handleSearchChange}
              ariaLabel={t("clients.aria.search", { name: "signed-waivers" })}
              ariaControls={t("clients.aria.search_result", { name: "signed-waivers" })}
              placeholder={t("global.search")}
            />
            <WithEmptyState
              name="Waiver"
              hasItems={data?.length > 0}
              fallbackTitle={isFiltered ? t("client.no_results_found") : t("client.empty_waiver_history")}
              fallbackImageSrc={null}
              className="overflow-auto"
            >
              <WaiversTable
                data={data}
                page={page}
                pageSize={pageSize}
                metadata={metadata}
                sortKey={sortKey}
                sortOrder={sortOrder}
                onPageChange={(p) => localDispatch({ type: ACTIONS.SET_PAGE, payload: p })}
                onPageSizeChange={(p) => localDispatch({ type: ACTIONS.SET_PAGE_SIZE, payload: p })}
                onSortChange={handleSortChange}
                onRowClick={handleRowClick}
                setIsAppend={(v) => localDispatch({ type: ACTIONS.SET_APPEND, payload: v })}
              />
            </WithEmptyState>
          </Flex>
        </Card>
      </FirstLoading>

      <Modal
        open={!!selectedWaiver}
        onCancel={handleCloseModal}
        width={800}
        title={
          <Flex vertical gap={4}>
            <Title level={4} className="mb-0">{selectedWaiver?.waiver_name}</Title>
            {selectedWaiver?.latest_signed_on && (
              <Text type="secondary">
                {t("client.waivers.modal.signed_on", {
                  date: moment(selectedWaiver.latest_signed_on, "MM/DD/YYYY").format("MMMM DD, YYYY")
                })}
              </Text>
            )}
          </Flex>
        }
      >
        {selectedWaiver && (
          pdfUrls[docIndex]
            ? <PdfViewer url={pdfUrls[docIndex]} height={500} />
            : (
              <Flex justify="center" align="center" style={{ height: 200 }}>
                <Text type="secondary">{t("client.waivers.modal.no_documents")}</Text>
              </Flex>
            )
        )}
      </Modal>
    </>
  )
}

WaiversTab.propTypes = {
  active: PropTypes.bool,
  id: PropTypes.string
}

export default WaiversTab
